/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useLayer } from '@coursera-web/statsig';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2, breakpoints } from '@coursera/cds-core';

import AuthenticationV3CtaTracking from 'bundles/authentication-common/components/AuthenticationV3CtaTracking';
import LinkButton from 'bundles/authentication-common/components/LinkButton';
import SSOEnforcementBanner from 'bundles/authentication-common/components/SSOEnforcementBanner';
import Separator from 'bundles/authentication-common/components/Separator';
import SocialSSO from 'bundles/authentication-common/components/authenticationModal/SocialSSO';
import UnifiedAuthEntryForm from 'bundles/authentication-common/components/forms/UnifiedAuthEntryForm';
import type SSOError from 'bundles/authentication-common/components/sso/SSOError';
import {
  AUTHENTICATION_FORM_VARIANTS,
  AUTHENTICATION_MODAL_MODES,
  type AUTHENTICATION_MODAL_MODES_KEY,
  LEARNER_HELP_CENTER_LINK,
  type LoginMethodType,
  SSO_BANNER_NAMES,
} from 'bundles/authentication-common/constants';
import type { EnterpriseSsoLoginInfo } from 'bundles/authentication-common/hooks/useEnterpriseSsoCheck';
import { shouldShowTOSDisclaimer } from 'bundles/authentication-common/utils';
import { loginMethodsToAuthModeWithMetadata } from 'bundles/authentication-common/utils/navigationUtils';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import useSaveOnboardingOnSignup from 'bundles/onboarding-common/hooks/useSaveOnboardingOnSignup';

import _t from 'i18n!nls/authentication';

export type Props = {
  className?: string;
  navigate: (
    path: AUTHENTICATION_MODAL_MODES_KEY,
    options?: Record<string, unknown>,
    shouldSwitchFormsByQueryParam?: boolean
  ) => void;
  onSSOSuccess: (isRegistration: boolean, hasSavedOnboardingOnSignup?: boolean) => void;
  onSSOError: (error: SSOError) => void;
  enterpriseSsoEmailCheckInfo: {
    onEmailChange: (email: string) => void;
    enterpriseSsoLoginInfo: EnterpriseSsoLoginInfo;
    checkForceSSO: (email: string) => Promise<void>;
  };
  title?: string;
  tagline?: string;
  authMode?: AUTHENTICATION_MODAL_MODES_KEY;
  ssoError?: SSOError;
  resetSsoError?: () => void;
};

const TrackedLinkButton = withSingleTracked({ type: 'BUTTON' })(LinkButton);

const styles = {
  container: css`
    display: grid;
    grid-template-columns: 1fr;
    margin: 0 auto;
    max-width: 396px;
    padding: 0;

    @media (min-width: 600px) {
      padding: var(--cds-spacing-50);
    }

    .cds-text-field-root {
      margin-top: var(--cds-spacing-50) !important;
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-300) var(--cds-spacing-200);
    }
  `,
  tagLine: css`
    margin: var(--cds-spacing-50) auto 0;
    width: 100%;
  `,
  title: css`
    margin: 0;
  `,
  link: css`
    text-decoration: underline;
  `,
  organization: css`
    margin: var(--cds-spacing-200) 0 var(--cds-spacing-150) 0;
  `,
  terms: css`
    margin: var(--cds-spacing-150) 0 0;
    padding: 0;

    &:first-of-type {
      margin: 0;
    }

    &:nth-of-type(n) a {
      color: inherit;
      text-decoration: underline;
    }
  `,
  header: css`
    margin-bottom: var(--cds-spacing-150);
  `,
  errorBanner: css`
    margin-bottom: var(--cds-spacing-200);
  `,
};

const UnifiedAuthEntry = ({
  navigate,
  onSSOSuccess,
  onSSOError,
  className,
  title,
  tagline,
  authMode,
  enterpriseSsoEmailCheckInfo,
  ssoError,
  resetSsoError,
}: Props) => {
  const layer = useLayer('lo_registration_aa_layer');
  // Statsig exposure logged here
  layer.get('is_enabled', 'variant1');
  const { saveOnboardingOnSignup } = useSaveOnboardingOnSignup();
  const { checkForceSSO, enterpriseSsoLoginInfo, onEmailChange } = enterpriseSsoEmailCheckInfo;
  const { isEnterpriseSsoLogin } = enterpriseSsoLoginInfo;

  const renderSsoError = () => {
    if (!ssoError) return null;

    if (ssoError.code === 'loginDisabledForDomain') {
      return (
        <SSOEnforcementBanner bannerName={SSO_BANNER_NAMES.SOCIAL_LOGIN} css={styles.errorBanner}>
          {_t(
            'It seems you tried to log in using social login. This email is associated with an account that requires email and password authentication.'
          )}
        </SSOEnforcementBanner>
      );
    } else if (ssoError.code === 'enforceSsoV2') {
      return (
        <SSOEnforcementBanner bannerName={SSO_BANNER_NAMES.SOCIAL_LOGIN} css={styles.errorBanner}>
          {_t('This email is associated with an account that requires SSO. Please continue with SSO.')}
        </SSOEnforcementBanner>
      );
    } else if (ssoError.code === 'invalidDomain') {
      return (
        <SSOEnforcementBanner bannerName={SSO_BANNER_NAMES.INVALID_DOMAIN} css={styles.errorBanner}>
          {_t(
            "We couldn't verify your email domain. Login or create an account using another login method. Contact your admin if the issue persists."
          )}
        </SSOEnforcementBanner>
      );
    }

    return null;
  };

  const navigateToOrganizationSSO = () => {
    navigate(AUTHENTICATION_MODAL_MODES.ORGANIZATIONSIGNUP, { referrer: AUTHENTICATION_MODAL_MODES.SIGNUP });
  };

  const handleOnSuccess = ({ loginMethods, email }: { loginMethods: Array<LoginMethodType>; email: string }) => {
    if (isEnterpriseSsoLogin) {
      return navigate(
        AUTHENTICATION_MODAL_MODES.LOGINCONTINUE,
        { defaultEmail: email, formVariant: AUTHENTICATION_FORM_VARIANTS.PASSWORD },
        false
      );
    }
    const {
      mode,
      navigationOptions: options,
      shouldSwitchFormsByQueryParam,
    } = loginMethodsToAuthModeWithMetadata({ loginMethods, email });

    return navigate(mode, options, shouldSwitchFormsByQueryParam);
  };

  const handleOnError = (email: string) => {
    navigate(
      authMode === AUTHENTICATION_MODAL_MODES.SIGNUP
        ? AUTHENTICATION_MODAL_MODES.SIGNUPCONTINUE
        : AUTHENTICATION_MODAL_MODES.LOGINCONTINUE,
      { defaultEmail: email, formVariant: AUTHENTICATION_FORM_VARIANTS.PASSWORD },
      false
    );
  };

  const handleSSOSuccess = async (isRegistration: boolean) => {
    const hasSavedOnboardingOnSignup = await saveOnboardingOnSignup();
    onSSOSuccess(isRegistration, hasSavedOnboardingOnSignup);
  };

  return (
    <section css={styles.container} className={className} data-catchpoint="authentication-box-content">
      <header css={styles.header}>
        <Typography2 component="h2" variant="titleXSmall" align="left" css={styles.title}>
          {title ?? _t('Log in or create account')}
        </Typography2>
        <Typography2 component="p" variant="bodyPrimary" color="supportText" align="left" css={styles.tagLine}>
          {tagline ?? (
            <FormattedMessage message={_t('Learn on your own time from top \n universities and businesses.')} />
          )}
        </Typography2>
      </header>
      {renderSsoError()}
      <div>
        <UnifiedAuthEntryForm
          onSuccess={handleOnSuccess}
          checkForceSSO={checkForceSSO}
          onError={handleOnError}
          ssoError={ssoError}
          resetSsoError={resetSsoError}
          onEmailChange={onEmailChange}
        />
        <Separator text={_t('or')} customStyles={{ margin: '22px 0' }} />
        <SocialSSO onError={onSSOError} onSuccess={handleSSOSuccess} />

        <Typography2 component="p" variant="bodySecondary" align="left" css={styles.organization}>
          <AuthenticationV3CtaTracking>
            {(trackV3) => {
              const handleOnClick = () => {
                trackV3({ name: 'signup_with_other_sso' });
                resetSsoError?.();
                navigateToOrganizationSSO();
              };
              return (
                <TrackedLinkButton
                  onClick={handleOnClick}
                  trackingName="sign_up_with_organization_btn_click"
                  css={styles.link}
                >
                  {_t('Sign up with your organization')}
                </TrackedLinkButton>
              );
            }}
          </AuthenticationV3CtaTracking>
        </Typography2>
      </div>
      <div className="auth-form-tos-section">
        <Typography2 component="p" variant="bodyTertiary" color="supportText" align="left" css={styles.terms}>
          {shouldShowTOSDisclaimer() && (
            <FormattedMessage
              message={_t("I accept Coursera's {tosLink} and {privacyLink}. ")}
              privacyLink={
                <a href="/about/privacy" target="_blank">
                  {_t('Privacy Notice')}
                </a>
              }
              tosLink={
                <a href="/about/terms" target="_blank">
                  {_t('Terms of Use')}
                </a>
              }
            />
          )}
          <FormattedMessage
            message={_t('Having trouble logging in? {helpCenterLink}')}
            helpCenterLink={
              <a href={LEARNER_HELP_CENTER_LINK} target="_blank" rel="noopener noreferrer">
                {_t('Learner help center')}
              </a>
            }
          />
        </Typography2>
        <Typography2 component="p" variant="bodyTertiary" color="supportText" align="left" css={styles.terms}>
          <FormattedMessage
            message={_t(
              'This site is protected by reCAPTCHA Enterprise and the Google {privacyPolicyLink} and {tosLink} apply.'
            )}
            privacyPolicyLink={
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                {_t('Privacy Policy')}
              </a>
            }
            tosLink={
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                {_t('Terms of Service')}
              </a>
            }
          />
        </Typography2>
      </div>
    </section>
  );
};

export default UnifiedAuthEntry;
